import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { ActivityFragment, EventFragment } from "../../__generated__/graphql";
import { formatDateWithDay, formatTime, formatDate } from "../../lib/utils";

// Design system colors
const COLORS = {
  accent: "#cdbf81",
  primary: "#1a1a1a",
  secondary: "#6b7280",
  tertiary: "#9ca3af",
  border: "#e5e7eb",
};

// Mapping between KontrolIcon values and emojis
const ICON_TO_EMOJI: Record<string, string> = {
  general: "📅",
  concert: "🎵",
  show: "🎭",
  train: "🚃",
  airplane: "✈️",
  accommodation: "🛏️",
  promotion: "🎤",
  marketing: "📢",
  special: "⭐",
  bus: "🚌",
  repeat: "🎙️",
  travel: "🧳",
  other: "📦",
  online: "🌐",
  lunch: "🍽️",
  meeting: "👥",
  tv: "📺",
  cinema: "🎬",
};

// Check if a string contains an emoji
function containsEmoji(text: string): boolean {
  const emojiRegex =
    /[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F600}-\u{1F64F}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2300}-\u{23FF}]|[\u{2B50}]|[\u{1FA00}-\u{1FAFF}]|[\u{FE00}-\u{FE0F}]|[\u{200D}]/u;
  return emojiRegex.test(text);
}

// Get activity title with emoji prefix if needed
function getActivityTitleWithEmoji(title: string, icon: string): string {
  if (containsEmoji(title)) {
    return title;
  }
  const emoji = ICON_TO_EMOJI[icon] || ICON_TO_EMOJI["general"];
  return `${emoji} ${title}`;
}

interface ExportUserInfo {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
}

interface ExportOrganizationInfo {
  name?: string | null;
}

export interface PDFExportOptions {
  userInfo?: ExportUserInfo | null;
  organizationInfo?: ExportOrganizationInfo | null;
}

// Helper to build full address from location
function buildFullAddress(location: {
  label?: string | null;
  address?: string | null;
  zipCode?: string | null;
  city?: string | null;
  countryCode?: string | null;
}): string {
  const parts: string[] = [];

  if (location.label) {
    parts.push(location.label);
  }

  if (location.address) {
    parts.push(location.address);
  }

  return parts.join(", ");
}

// Generate HTML content for the PDF
function generateHTMLContent(
  event: EventFragment,
  activities: ActivityFragment[],
  options?: PDFExportOptions
): string {
  // Group activities by date
  const activitiesByDate: Record<string, ActivityFragment[]> = {};

  activities.forEach((activity) => {
    const dateKey = formatDateWithDay(activity.startDate);
    if (!activitiesByDate[dateKey]) {
      activitiesByDate[dateKey] = [];
    }
    activitiesByDate[dateKey].push(activity);
  });

  // Sort activities within each date
  Object.keys(activitiesByDate).forEach((date) => {
    activitiesByDate[date].sort(
      (a, b) =>
        new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
    );
  });

  // Sort dates
  const sortedDates = Object.keys(activitiesByDate).sort((a, b) => {
    const dateA = new Date(activitiesByDate[a][0].startDate);
    const dateB = new Date(activitiesByDate[b][0].startDate);
    return dateA.getTime() - dateB.getTime();
  });

  // Build user/org info HTML
  let userOrgHTML = "";
  if (options?.organizationInfo?.name || options?.userInfo) {
    userOrgHTML = `<div style="text-align: right; font-size: 11px; color: ${COLORS.secondary};">`;
    if (options.organizationInfo?.name) {
      userOrgHTML += `<div style="font-weight: bold; margin-bottom: 2px;">${escapeHtml(options.organizationInfo.name)}</div>`;
    }
    if (options.userInfo) {
      const userName = [options.userInfo.firstName, options.userInfo.lastName]
        .filter(Boolean)
        .join(" ");
      if (userName) {
        userOrgHTML += `<div>${escapeHtml(userName)}</div>`;
      }
      if (options.userInfo.email) {
        userOrgHTML += `<div>${escapeHtml(options.userInfo.email)}</div>`;
      }
      if (options.userInfo.phoneNumber) {
        userOrgHTML += `<div>${escapeHtml(options.userInfo.phoneNumber)}</div>`;
      }
    }
    userOrgHTML += `</div>`;
  }

  // Event date and location
  const dateText = event.startDate
    ? `${formatDate(event.startDate)}${event.endDate ? ` - ${formatDate(event.endDate)}` : ""}`
    : "";
  const locationText = event.eventLocation?.location
    ? buildFullAddress(event.eventLocation.location)
    : "";

  // Build activities HTML
  let activitiesHTML = "";
  for (const date of sortedDates) {
    const dateActivities = activitiesByDate[date];

    activitiesHTML += `
      <div style="margin-top: 20px;">
        <div style="font-weight: bold; font-size: 14px; color: ${COLORS.accent}; margin-bottom: 4px;">
          ${escapeHtml(date)}
        </div>
        <div style="border-bottom: 1px solid ${COLORS.border}; margin-bottom: 10px;"></div>
    `;

    for (const activity of dateActivities) {
      const timeText = `${formatTime(activity.startDate)}${activity.endDate ? ` - ${formatTime(activity.endDate)}` : ""}`;
      const activityLocation = activity.activityLocation?.location
        ? buildFullAddress(activity.activityLocation.location)
        : "";

      let participantsText = "";
      if (
        activity.activityParticipants &&
        activity.activityParticipants.length > 0
      ) {
        const names = activity.activityParticipants
          .slice(0, 3)
          .map((p) => p.publicUserOrganization?.name || "")
          .filter(Boolean)
          .join(", ");
        if (names) {
          participantsText =
            activity.activityParticipants.length > 3
              ? `Participants: ${names} +${activity.activityParticipants.length - 3}`
              : `Participants: ${names}`;
        }
      }

      activitiesHTML += `
        <div style="display: flex; margin-bottom: 12px; page-break-inside: avoid;">
          <div style="width: 4px; background-color: ${activity.color || COLORS.accent}; margin-right: 12px; flex-shrink: 0;"></div>
          <div style="flex: 1;">
            <div style="font-weight: bold; font-size: 12px; color: ${COLORS.primary}; margin-bottom: 2px;">
              ${escapeHtml(timeText)}
            </div>
            <div style="font-weight: bold; font-size: 14px; color: ${COLORS.primary}; margin-bottom: 4px;">
              ${escapeHtml(getActivityTitleWithEmoji(activity.title, activity.icon))}
            </div>
            ${activityLocation ? `<div style="font-size: 11px; color: ${COLORS.secondary}; margin-bottom: 2px;">${escapeHtml(activityLocation)}</div>` : ""}
            ${participantsText ? `<div style="font-size: 10px; font-style: italic; color: ${COLORS.tertiary};">${escapeHtml(participantsText)}</div>` : ""}
          </div>
        </div>
      `;
    }

    activitiesHTML += `</div>`;
  }

  return `
    <div style="font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; padding: 30px; width: 595px; background: white; color: ${COLORS.primary};">
      <!-- Header -->
      <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 10px;">
        <div style="flex: 1;">
          <h1 style="font-size: 24px; font-weight: bold; margin: 0 0 8px 0; color: ${COLORS.primary};">
            ${escapeHtml(event.name)}
          </h1>
          ${dateText ? `<div style="font-size: 13px; color: ${COLORS.secondary}; margin-bottom: 4px;">${escapeHtml(dateText)}</div>` : ""}
          ${locationText ? `<div style="font-size: 13px; color: ${COLORS.secondary};">${escapeHtml(locationText)}</div>` : ""}
        </div>
        ${userOrgHTML}
      </div>
      
      <!-- Accent line -->
      <div style="height: 2px; background-color: ${COLORS.accent}; margin: 15px 0;"></div>
      
      <!-- Activities -->
      ${activitiesHTML}
    </div>
  `;
}

// Escape HTML special characters
function escapeHtml(text: string): string {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
}

export async function generateItineraryPDF(
  event: EventFragment,
  activities: ActivityFragment[],
  options?: PDFExportOptions
) {
  // Create a temporary container for rendering
  const container = document.createElement("div");
  container.style.position = "absolute";
  container.style.left = "-9999px";
  container.style.top = "0";
  container.innerHTML = generateHTMLContent(event, activities, options);
  document.body.appendChild(container);

  try {
    const content = container.firstElementChild as HTMLElement;

    // Render HTML to canvas
    const canvas = await html2canvas(content, {
      scale: 2, // Higher quality
      useCORS: true,
      logging: false,
      backgroundColor: "#ffffff",
    });

    // Create PDF
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 10;
    const contentWidth = pageWidth - margin * 2;

    // Calculate dimensions
    const imgWidth = contentWidth;
    const imgHeight = (canvas.height * contentWidth) / canvas.width;

    // Split into pages if needed
    let yPosition = margin;
    let remainingHeight = imgHeight;
    let sourceY = 0;

    while (remainingHeight > 0) {
      const availableHeight = pageHeight - margin * 2 - 10; // Leave space for footer
      const heightToDraw = Math.min(remainingHeight, availableHeight);

      // Calculate source dimensions
      const sourceHeight = (heightToDraw / imgHeight) * canvas.height;

      // Create a temporary canvas for this page section
      const pageCanvas = document.createElement("canvas");
      pageCanvas.width = canvas.width;
      pageCanvas.height = sourceHeight;
      const ctx = pageCanvas.getContext("2d");

      if (ctx) {
        ctx.drawImage(
          canvas,
          0,
          sourceY,
          canvas.width,
          sourceHeight,
          0,
          0,
          canvas.width,
          sourceHeight
        );

        const pageImgData = pageCanvas.toDataURL("image/png");
        pdf.addImage(pageImgData, "PNG", margin, yPosition, imgWidth, heightToDraw);
      }

      remainingHeight -= heightToDraw;
      sourceY += sourceHeight;

      if (remainingHeight > 0) {
        pdf.addPage();
        yPosition = margin;
      }
    }

    // Add footer to all pages
    const totalPages = pdf.internal.pages.length - 1;
    const todayFormatted = formatDateWithDay(new Date().toISOString());
    const linkText = `Généré depuis Kontrol le ${todayFormatted}`;
    const kontrolUrl = "https://www.kontrol-app.fr";

    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i);

      // "Kontrol" in bold
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(10);
      pdf.setTextColor(COLORS.primary);
      pdf.text("Kontrol", pageWidth / 2, pageHeight - 12, { align: "center" });

      // Link text
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8);
      pdf.setTextColor(COLORS.accent);
      pdf.text(linkText, pageWidth / 2, pageHeight - 7, { align: "center" });

      // Add clickable link
      const linkWidth = pdf.getTextWidth(linkText);
      const linkX = (pageWidth - linkWidth) / 2;
      pdf.link(linkX, pageHeight - 10, linkWidth, 5, { url: kontrolUrl });
    }

    // Download the PDF
    const fileName = `${event.name.replace(/[^a-zA-Z0-9]/g, "_")}_itinerary.pdf`;
    pdf.save(fileName);
  } finally {
    // Clean up
    document.body.removeChild(container);
  }
}
